
import { of as observableOf, combineLatest, EMPTY } from 'rxjs';

import { catchError, concatAll, mergeMap, map, switchMap, withLatestFrom, tap, startWith } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import {
    AddCatalogueItems,
    AddLoadedCategoryId,
    LOAD_CATALOGUE_ITEMS,
    LOAD_CATEGORIES,
    LoadCatalogueItems,
    LoadCatalogueItemsFail,
    LoadCatalogueItemsSuccess,
    LoadCategoriesFail,
    LoadCategoriesSuccess
} from './sampling-supplies.actions';

import { environment } from '../../../environments/environment';
import { HeadersEnum, ParamsKeys, ParamsValues, RestURLsEnum } from '../../model/http-request-enums';
import { SamplingSuppliesCategory } from '../../model/sampling-supply-category.model';
import { Router } from '@angular/router';
import { CatalogueItem } from '../../model/catalogue-category/catalogue-item.model';
import { RootCategoryNodes } from '../../model/catalogue-category/root-category-nodes.model';
import { Category } from '../../model/catalogue-category/category.model';
import { CategoryDescription } from '../../model/catalogue-category/category-description.model';
import { Price } from '../../model/catalogue-category/price.model';
import { Action } from '@ngrx/store';
import { InitPersonaSuccess, PersonaActionTypes, SetPersona } from '@app/persona/store/personas/persona.actions';
import { PersonaTypes } from '@app/persona/constants/constants';
import { Persona } from '@app/persona/models';
import { NoopAction } from '@app/store/app.reducers';

interface destructuredCategoryType {
    category: CategoryDescription;
    childCategories: Category[];
}

interface destructuredCatalogueType {
    id: number;
    name: string;
    code: string;
    description: string;
    quantity: number;
    price: Price;
    resourceType: string;
}

export const flattenCategory = ({ category, childCategories }: destructuredCategoryType): SamplingSuppliesCategory => {
    const { id, name } = category;
    let samplingSuppliesChildCategories: SamplingSuppliesCategory[] = [];
    if (childCategories.length > 0) {
        samplingSuppliesChildCategories = childCategories.map((childCategory: Category) => flattenCategory(childCategory));
    }
    return new SamplingSuppliesCategory(id, name, samplingSuppliesChildCategories);
};

const getApplicablePersonaIds = (persona: Persona) => {
    const isCatalogueFilter = personaArg => personaArg.type === PersonaTypes.FILTER;
    return !!persona && isCatalogueFilter(persona) ? persona.personaOrganisationGroupIds : [];
};

@Injectable()
export class SamplingSuppliesEffects {
    categoryURL = environment.csBaseURL + RestURLsEnum.CATEGORY_BASE;

    header = new HttpHeaders().set(ParamsKeys.ACCEPT,
        [HeadersEnum.ACCEPT_CATEGORY, HeadersEnum.ACCEPT_ERROR].join(','));

    loadCatalogueItems$ = createEffect(() => combineLatest([
        this.actions$.pipe(ofType(LOAD_CATEGORIES), startWith(new NoopAction())),
        this.actions$.pipe(ofType(PersonaActionTypes.SET_PERSONA, PersonaActionTypes.INIT_PERSONA_SUCCESS))
    ]).pipe(
        switchMap(([, { persona }]: [Action, SetPersona | InitPersonaSuccess]) => {

            const personaIds = getApplicablePersonaIds(persona);
            let params = new HttpParams();

            for (const personaId of personaIds) {
                params = params.append(ParamsKeys.ORGANIZATION_GROUP_IDS, `${personaId}`);
            }

            return this.httpClient.get(this.categoryURL, { params })
                .pipe(
                    catchError(_ => observableOf(new LoadCategoriesFail())));
        }),
        map((response: RootCategoryNodes | LoadCategoriesFail) => {
            if (response instanceof LoadCategoriesFail) {
                this.router.navigate(['/error']);
                return response;
            }
            const { nodes }: { nodes: Category[] } = response;
            const categories = nodes.map((item: Category) => flattenCategory(item));
            return new LoadCategoriesSuccess(categories);
        }
        )
    )
    );


    onLoadCatalogueItems = createEffect(() => this.actions$.pipe(ofType(LOAD_CATALOGUE_ITEMS)).pipe(
        withLatestFrom(this.actions$.pipe(ofType(PersonaActionTypes.SET_PERSONA, PersonaActionTypes.INIT_PERSONA_SUCCESS))),
        mergeMap(([action, { persona }]: [LoadCatalogueItems, SetPersona|InitPersonaSuccess]) => {
            const personaIds = getApplicablePersonaIds(persona);
            let params = new HttpParams();

            for (const personaId of personaIds) {
                params = params.append(ParamsKeys.ORGANIZATION_GROUP_IDS, `${personaId}`);
            }

            params = params.set(ParamsKeys.EXCLUDE, ParamsValues.NO_PRICE)
                .set(ParamsKeys.INCLUDE, [ParamsValues.PRICES, ParamsValues.CATALOGUE_ITEMS].join(','))
                .set(ParamsKeys.CHILD_DEPTH, '1')
                .set(ParamsKeys.SORT_BY, [ParamsValues.CATEGORY_NAME, ParamsValues.CATALOGUE_ITEM_NAME_ASC].join(','));

            const url = `${this.categoryURL}/${action.categoryId}`;

            return this.httpClient.get<Category>(url,
                {
                    params: params
                }).pipe(
                catchError(_ => observableOf(new LoadCatalogueItemsFail(action.categoryId))));
        }),
        map((response: LoadCatalogueItemsFail | Category) => {
            if (response instanceof LoadCatalogueItemsFail) {
                this.router.navigate(['/error']);
                return [response];
            }

            const { category, catalogueItems }: { category: CategoryDescription; catalogueItems: CatalogueItem[] } = response;
            const categoryId = category.id;
            const items = catalogueItems.map(
                ({
                    id,
                    name,
                    code,
                    description,
                    quantity,
                    price,
                    resourceType
                }: destructuredCatalogueType) => new CatalogueItem(
                    id,
                    name,
                    code,
                    description,
                    quantity,
                    price,
                    [],
                    categoryId,
                    resourceType
                )
            );

            const itemIds = items.map(item => item.id);

            return [
                new LoadCatalogueItemsSuccess(categoryId, itemIds),
                new AddCatalogueItems(items),
                new AddLoadedCategoryId(categoryId)
            ];
        }
        ),
        concatAll()
    ));

    constructor(private actions$: Actions,
        private httpClient: HttpClient,
        private router: Router) {
    }
}




